import * as React from 'react';
import { Checkbox, CheckboxProps, MuiThemeProvider } from '@material-ui/core';
import { ICellRendererParams } from '@ag-grid-community/core';
import { muiTheme } from 'src/utils/Style/Theme';
import { style } from 'typestyle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox';
import { StatusIconRenderer } from 'src/components/StatusIconRenderer/StatusIconRenderer';

type Props = ICellRendererParams & {
  value?: boolean;
  onChange?: (checked: boolean, rowData?: any) => void;
  isEditable: boolean;
  allowIndeterminate?: boolean;
  icons?: {
    falsy: string;
    truthy: string;
    indeterminate: string;
  };
};

type State = {};

const checkStyle = style({
  $debugName: 's5-checkbox',
  width: '100%',
  textAlign: 'center',
});

export default class CheckboxCellRenderer extends React.Component<Props, State> {
  checkMap = {
    on: {
      checked: true,
      indeterminate: false,
    },
    off: {
      checked: false,
      indeterminate: false,
    },
    indeterminate: {
      checked: false,
      indeterminate: true,
    },
  };
  constructor(props: Props) {
    super(props);

    // for `allowIndeterminate` we assume/require the values are sent in
    // in valid boolean format.
  }

  componentWillUnmount() {
    // Any destruction calls need to be done here, not in destroy()
  }

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (this.props.onChange) {
      this.props.onChange(event.target.checked);
    }

    if (this.props.isEditable) {
      if (this.props.setValue) {
        this.props.setValue(!this.props.value);
      }
    }
  };

  getValue() {
    return this.props.value;
  }

  isPopup() {
    return false;
  }

  // this is how we determine indeterminance for standard checkboxes
  // if value is not true/false it's "indeterminant" (any other than
  // checked/notchecked essentially)
  getCheckSetting = (checked: unknown) => {
    if (checked === true || checked === 1 || checked === 'true') {
      return this.checkMap['on'];
    } else if (checked === false || checked === '') {
      return this.checkMap['off'];
    } else {
      return this.checkMap['indeterminate'];
    }
  };

  render() {
    const { isEditable, allowIndeterminate, icons, value } = this.props;
    let statusProps: Partial<CheckboxProps>;
    let uncheckedIcon = <RadioButtonUncheckedIcon />;
    let checkedIcon = <CheckCircleOutlineIcon />;
    let indeterminateIcon = <IndeterminateCheckBoxIcon />;

    if (allowIndeterminate) {
      statusProps = this.getCheckSetting(this.props.value);
    } else {
      statusProps = {
        checked: value === true || value === 1 || value === 'true' ? true : false,
      };
    }

    if (icons) {
      checkedIcon = icons.truthy ? <StatusIconRenderer value={`${this.props.icons?.truthy},#555`} /> : checkedIcon;
      uncheckedIcon = icons.falsy ? <StatusIconRenderer value={`${this.props.icons?.falsy},#555`} /> : uncheckedIcon;
      indeterminateIcon = icons.indeterminate ? (
        <StatusIconRenderer value={`${this.props.icons?.indeterminate},#555`} />
      ) : (
        indeterminateIcon
      );
    }

    return (
      <Checkbox
        icon={uncheckedIcon}
        checkedIcon={checkedIcon}
        indeterminateIcon={indeterminateIcon}
        className={checkStyle}
        onChange={this.handleChange}
        disabled={!isEditable}
        disableRipple={!isEditable}
        {...statusProps}
      />
    );
  }
}
